
import { computed, defineComponent, onBeforeMount, reactive } from 'vue'
import { useRoute, useRouter } from 'vue-router'

// @components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import Checkbox from '@/common/components/UI/Checkbox/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import Loader from '@/common/components/UI/Loader/index.vue'

// @types
import type { DispatcherSector } from '@/new-services/types/dispatcher-sectors'
import type { Period, UpdatePeriodPayload } from '@/new-services/types/periods'

// @new-version-components
import { NvCalendar, NvPeriodBadge } from '@/new-version/components'

// @new-services
import dispatcherSectorsAPI from '@/new-services/dispatcher-sectors.service'
import periodsAPI from '@/new-services/periods.service'

// @composable
import useNotifications from '@/common/composable/useNotifications'

// @validations
import useVuelidate from '@vuelidate/core'
import { required } from '@vuelidate/validators'

export default defineComponent({
    components: {
        PageTitle,
        TheButton,
        Checkbox,
        TheInput,
        Loader,
        NvCalendar,
        NvPeriodBadge,
    },
    setup() {
        const route = useRoute()
        const router = useRouter()
        const notifications = useNotifications()

        const periodId = Number(route.params.id as string)

        const state = reactive({
            is_loading: false,
            is_active: true,
            name: null as string | null,
            dispatcherSectorIds: [] as Array<number>,
        })

        const state_form$ = useVuelidate({
            name: { required }
        }, state)

        const data_state = reactive({
            period: {
                is_loading: false,
                is_failed: false,
                item: null as Period | null
            },
            sectors: {
                is_loading: false,
                is_failed: false,
                list: [] as Array<DispatcherSector>
            },
        })

        const loadSectors = async () => {
            try {
                data_state.sectors.is_loading = true

                const response = await dispatcherSectorsAPI.getDispatcherSectors({ page: 1, limit: 9999 })
                data_state.sectors.list = response.data.data

                data_state.sectors.is_failed = false
            } catch (error) {
                console.error(error)
                notifications.notifyError({ error, title: 'При получении секторов произошла ошибка' })
                data_state.sectors.is_failed = true
            } finally {
                data_state.sectors.is_loading = false
            }
        }

        const loadPeriod = async () => {
            try {
                data_state.period.is_loading = true

                const response = await periodsAPI.getPeriodById(periodId)
                data_state.period.item = response.data.data

                state.name = response.data.data.name
                state.dispatcherSectorIds = response.data.data.sectors.map((sector) => sector.id)
                state.is_active = response.data.data.statusId === 41

                data_state.period.is_failed = false
            } catch (error) {
                console.error(error)
                notifications.notifyError({ error, title: 'При получении периода произошла ошибка' })
                data_state.period.is_failed = true
            } finally {
                data_state.period.is_loading = false
            }
        }

        const isSectorSaved = (sectorId: number) => {
            if (data_state.period.item) {
                return data_state.period.item.sectors.map((sector) => sector.id).includes(sectorId)
            }

            return false
        }

        const isSectorSelected = (sectorId: number) => {
            return state.dispatcherSectorIds.includes(sectorId)
        }

        const onSelectSector = (sectorId: number) => {
            if (isSectorSaved(sectorId)) return

            if (isSectorSelected(sectorId)) {
                const index = state.dispatcherSectorIds.findIndex((sector_id) => sector_id === sectorId)

                if (index >= 0) {
                    state.dispatcherSectorIds.splice(index, 1)
                }

                return
            }

            state.dispatcherSectorIds.push(sectorId)
        }

        const onSelectAllSectors = () => {
            if (isAllSectorsSelected.value) {
                if (data_state.period.item) {
                    state.dispatcherSectorIds = data_state.period.item.sectors.map((sector) => sector.id)
                }

                return
            }

            state.dispatcherSectorIds = data_state.sectors.list.map((sector) => sector.id)
        }

        onBeforeMount(() => {
            loadPeriod()
            loadSectors()
        })

        const onSubmit = async () => {
            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            if (state.dispatcherSectorIds.length === 0) {
                notifications.notify({ type: 'warning', title: 'Ошибка валидации', description: 'Выберите минимум 1 сектор' })
                return
            }

            try {
                state.is_loading = true

                const payload: UpdatePeriodPayload = {
                    name: state.name as string,
                    dispatcherSectorIds: state.dispatcherSectorIds,
                }

                await periodsAPI.updatePeriod(periodId, payload)

                notifications.notify({ type: 'success', title: 'Период сохранен' })
                router.push('/time-slots/periods')
            } catch (error) {
                console.error(error)
                notifications.notifyError({ error, title: 'При сохранении периода произошла ошибка' })
            } finally {
                state.is_loading = false
            }
        }

        const onToggleStatus = async () => {
            if (state.is_loading) return

            const answer = confirm(state.is_active ? `Вы действительно хотите деактивировать период ${state.name}?` : `Вы действительно хотите активировать период ${state.name}?`)
            if (!answer) return

            try {
                state.is_loading = true

                const statusId = state.is_active ? 42 : 41
                await periodsAPI.updatePeriodStatus(periodId, statusId)

                notifications.notify({ type: 'success', title: state.is_active ? 'Период деактивирован' : 'Период активирован' })
                loadPeriod()
            } catch (error) {
                console.error(error)
                notifications.notifyError({ error, title: 'При обновлении статуса периода произошла ошибка' })
            } finally {
                state.is_loading = false
            }
        }

        const isAllSectorsSelected = computed(() => data_state.sectors.list.map((sector) => sector.id).every((sectorId) => state.dispatcherSectorIds.includes(sectorId)))

        return {
            state,
            state_form$,
            data_state,
            loadPeriod,
            loadSectors,
            isSectorSaved,
            isSectorSelected,
            onSelectSector,
            onSelectAllSectors,
            onSubmit,
            onToggleStatus,
            isAllSectorsSelected,
        }
    }
})
